import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaCalendar,
  FaUser,
  FaTags,
  FaSync,
  FaEye,
  FaHeart,
} from "react-icons/fa";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface BlogPost {
  id: number;
  title: string;
  publishDate: string;
  tags: string;
  authorName: string;
  imageUrl: string;
  content: string;
  excerpt: string;
  isHighlighted: boolean;
  viewCount: number;
  loveCount: number;
  language: string;
  languageDisplay: string;
  category: string;
  categoryDisplay: string;
}

const BlogDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [blog, setBlog] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ✅ Track if view has been counted (prevents double increment)
  const viewCountedRef = useRef(false);

  // ✅ NEW: Love reaction state
  const [hasLoved, setHasLoved] = useState(false);
  const [loveCount, setLoveCount] = useState(0);
  const loveCountedRef = useRef(false);

  const fetchBlogDetails = async () => {
    setLoading(true);
    setError("");
    try {
      // ✅ Fetch blog data WITHOUT incrementing view count
      const response = await fetch(`${API_BASE}/api/public/blogs/${id}`, {
        credentials: "include",
      });

      if (response.status === 404) {
        setError("Blog post not found");
        return;
      }

      if (!response.ok) throw new Error("Failed to fetch blog details");

      const data: BlogPost = await response.json();
      setBlog(data);
      setLoveCount(data.loveCount || 0); // ✅ Initialize love count from API

      // ✅ Increment view count SEPARATELY (only once)
      if (!viewCountedRef.current) {
        await incrementViewCount();
        viewCountedRef.current = true;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load blog");
    } finally {
      setLoading(false);
    }
  };

  // ✅ NEW: Separate function to increment view count (using GET for public access)
  const incrementViewCount = async () => {
    try {
      await fetch(`${API_BASE}/api/public/blogs/${id}/view`, {
        method: "GET",
        credentials: "include",
      });
      console.log("✅ View count incremented");
    } catch (err) {
      console.error("⚠️ Failed to increment view count:", err);
      // Don't throw - view count is not critical
    }
  };

  // ✅ NEW: Increment love count
  const incrementLoveCount = async () => {
    if (hasLoved || !id) return;

    try {
      await fetch(`${API_BASE}/api/public/blogs/${id}/love`, {
        method: "GET",
        credentials: "include",
      });

      // Update local state
      setHasLoved(true);
      setLoveCount((prev) => prev + 1);

      // Save to localStorage
      localStorage.setItem(`blog_loved_${id}`, "true");

      console.log("✅ Love count incremented");
    } catch (err) {
      console.error("⚠️ Failed to increment love count:", err);
    }
  };

  // ✅ Check if user has already loved this blog (from localStorage)
  useEffect(() => {
    if (id) {
      const loved = localStorage.getItem(`blog_loved_${id}`) === "true";
      setHasLoved(loved);
    }
  }, [id]);

  useEffect(() => {
    // Reset view counted flag when blog ID changes
    viewCountedRef.current = false;
    fetchBlogDetails();
  }, [id]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const formatViewCount = (count: number) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  if (loading) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <FaSync className="animate-spin h-12 w-12 text-[#009fe3] mx-auto mb-4" />
          <p className="text-gray-600">Loading blog post...</p>
        </div>
      </section>
    );
  }

  if (error || !blog) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded mb-6">
            <p className="text-red-700 font-semibold">Error</p>
            <p className="text-red-600">{error || "Blog post not found"}</p>
          </div>
          <button
            onClick={() => navigate("/blog")}
            className="bg-[#009fe3] hover:bg-[#007cb5] text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2"
          >
            <FaArrowLeft /> Back to Blog
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4">
        {/* Back Button */}
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-[#009fe3] hover:text-[#007cb5] font-semibold mb-6 transition-colors"
        >
          <FaArrowLeft /> Back to Blog
        </Link>

        {/* Blog Content */}
        <article className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Featured Image (16:9 aspect ratio) */}
          {blog.imageUrl && (
            <div className="w-full aspect-video overflow-hidden">
              <img
                src={blog.imageUrl}
                alt={blog.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Blog Header */}
          <div className="p-8 md:p-12">
            {/* Badges */}
            <div className="flex gap-2 mb-4">
              {/* Category Badge */}
              <span className="inline-block bg-[#009fe3] text-white px-4 py-1.5 rounded-full text-sm font-semibold">
                {blog.categoryDisplay}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6 leading-tight">
              {blog.title}
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap gap-6 text-gray-600 mb-8 pb-6 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <FaCalendar className="text-[#009fe3]" />
                <span>{formatDate(blog.publishDate)}</span>
              </div>
              <div className="flex items-center gap-2">
                <FaUser className="text-[#009fe3]" />
                <span className="font-semibold">{blog.authorName}</span>
              </div>
              {blog.tags && (
                <div className="flex items-center gap-2">
                  <FaTags className="text-[#009fe3]" />
                  <span>{blog.tags}</span>
                </div>
              )}
              {/* View Count */}
              <div className="flex items-center gap-2">
                <FaEye className="text-[#009fe3]" />
                <span>{formatViewCount(blog.viewCount)} views</span>
              </div>
              {/* Love Count */}
              <button
                onClick={incrementLoveCount}
                disabled={hasLoved}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                  hasLoved
                    ? "bg-red-100 text-red-600 cursor-not-allowed"
                    : "bg-gray-100 text-gray-700 hover:bg-red-100 hover:text-red-600"
                }`}
                title={
                  hasLoved ? "You already loved this post" : "Love this post"
                }
              >
                <FaHeart className={hasLoved ? "text-red-600" : ""} />
                <span>{loveCount}</span>
              </button>
            </div>

            {/* Excerpt */}
            {blog.excerpt && (
              <div className="bg-[#e0f7ff] border-l-4 border-[#009fe3] p-6 rounded mb-8">
                <p className="text-lg text-gray-700 italic leading-relaxed">
                  {blog.excerpt}
                </p>
              </div>
            )}

            {/* Blog Content */}
            <div
              className="prose prose-lg max-w-none text-gray-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />
          </div>
        </article>

        {/* Back to Blog Button */}
        <div className="mt-8 text-center">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            <FaArrowLeft /> View All Blog Posts
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogDetails;
